import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Star, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';
import Header from '@components/layout/Header';
import Avatar from '@components/ui/Avatar';
import Spinner from '@components/ui/Spinner';
import api from '@utils/api';
import {
  combinedUserRatingPreview,
  formatCountNoun,
  formatReviewDate,
  singleReviewAverageScore,
} from '@utils/helpers';

const clampLines = 'line-clamp-4';

const ReviewBody = ({ text, reviewId, expanded, onToggle }) => {
  if (!text) return null;
  const long = text.length > 200;
  const showFull = expanded || !long;
  return (
    <div className="mt-2">
      <p className={`text-sm leading-relaxed text-surface-600 dark:text-surface-400 ${showFull ? '' : clampLines}`}>
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => onToggle(reviewId)}
          className="mt-1 text-sm font-bold text-brand-600 hover:text-brand-500 dark:text-brand-400"
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

const ReviewsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { siteOrigin } = usePublicConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 'notfound' | 'error' | null
  const [payload, setPayload] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!userId) {
        setError('notfound');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/users/${userId}/ratings?full=1`);
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'notfound' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
        <Header />
        <Spinner className="min-h-[50vh]" size="lg" />
      </div>
    );
  }

  if (!loading && !payload) {
    return (
      <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
        <Header />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <p className="text-surface-600 dark:text-surface-400">
            {error === 'notfound' ? 'User or reviews not found.' : 'Could not load reviews. Try again later.'}
          </p>
          <Link to="/feed" className="btn-primary mt-6 inline-flex">
            Back to feed
          </Link>
        </div>
      </div>
    );
  }

  const combined = combinedUserRatingPreview(payload);
  const total = payload.totalReviews ?? payload.recentReviews?.length ?? 0;
  const list = payload.recentReviews || [];

  return (
    <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
      <Header />
      <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(`/profile/${userId}`)}
          className="mb-6 inline-flex items-center gap-0.5 text-sm font-semibold text-surface-600 transition-colors hover:text-brand-600 dark:text-surface-400 dark:hover:text-brand-400"
        >
          <ChevronLeft size={18} strokeWidth={2.25} />
          Back to profile
        </button>

        <h1 className="text-xl font-extrabold text-surface-900 dark:text-white sm:text-2xl">Ratings & Reviews</h1>
        <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
          {payload.reviewedUserName || 'Member'}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3.5 py-2 text-sm font-bold text-white shadow-sm dark:bg-teal-500">
            {combined > 0 ? combined.toFixed(1) : '—'}
            <Star size={16} className="fill-white text-white" aria-hidden />
          </span>
          <span className="inline-flex items-center rounded-full border border-surface-200 bg-white px-3.5 py-2 text-sm font-medium text-surface-600 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-300">
            {formatCountNoun(total, 'review')}
          </span>
        </div>

        <h2 className="mt-10 text-sm font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
          All reviews ({list.length})
        </h2>

        {list.length === 0 ? (
          <p className="mt-4 text-sm text-surface-500 dark:text-surface-400">No written reviews yet.</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {list.map((r) => {
              const score = singleReviewAverageScore(r);
              const rid = r._id;
              return (
                <li
                  key={rid}
                  className="card border-surface-200/80 !p-5 dark:border-surface-800"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-100 pb-3 dark:border-surface-800">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white dark:bg-emerald-500">
                        {score > 0 ? `${score.toFixed(1)}★` : '—'}
                      </span>
                      <span className="text-xs text-surface-400">{formatReviewDate(r.createdAt)}</span>
                    </div>
                  </div>
                  <ReviewBody
                    text={r.comment}
                    reviewId={rid}
                    expanded={Boolean(expandedIds[rid])}
                    onToggle={toggleExpand}
                  />
                  <div className="mt-4 flex items-center gap-2">
                    <Avatar
                      name={r.reviewerId?.name}
                      src={resolveProfileDisplaySrc(r.reviewerId, siteOrigin)}
                      size="sm"
                    />
                    {r.reviewerId?.faceVerified && (
                      <CheckCircle2
                        size={16}
                        className="shrink-0 text-emerald-600 dark:text-emerald-400"
                        title="Face verified"
                        aria-label="Face verified"
                      />
                    )}
                    <span className="truncate text-sm font-semibold text-surface-900 dark:text-white">
                      {r.reviewerId?.name || 'Member'}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

/** `/reviews` → current user’s reviews, or login if guest. */
export const ReviewsIndexRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
        <Header />
        <Spinner className="min-h-[40vh]" size="lg" />
      </div>
    );
  }
  if (!user?._id) {
    return <Navigate to="/login" replace state={{ from: { pathname: '/reviews' } }} />;
  }
  return <Navigate to={`/reviews/${user._id}`} replace />;
};

export default ReviewsPage;
